import clsx from 'clsx'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import Avatar from '@/components-latest/primitives/Avatar'
import ContentBlock from '@/components-latest/primitives/ContentBlock'
import {
  Alert,
  Edit,
  EyeOff,
  EyeOpen
} from '@/components-latest/primitives/Icons'
import OptionButton from '@/components-latest/primitives/OptionButton'
import Text from '@/components-latest/primitives/Text'
import HideMenuModal from '@/components-latest/ui/MenuPage/HideMenuModal'
import { IError } from '@/config/http/http.helpers'
import { SupportedLanguages } from '@/config/i18n'
import { selectAppSettings } from '@/ducks/app/selectors/app.selectors'
import {
  useHideMenuItemMutation,
  useShowMenuItemMutation
} from '@/ducks/menu/api/menu.api'
import { IMenuItem } from '@/ducks/menu/menu.types'
import { addSystemNotification } from '@/ducks/notifications/slices/notification.slice'
import {
  selectCurrencyCode,
  selectSelectedBranchId,
  selectStoreId
} from '@/ducks/store/selectors/store.selectors'
import { formatPrice } from '@/utils/currency.utils'

import Styles from './MenuItem.module.scss'

type Props = {
  className?: string
  menuItem: IMenuItem
  disabled?: boolean
  editOffer?: (itemId: string) => void
  disableOffer?: (itemId: string) => void
}

const MenuItem = React.memo(
  ({
    className,
    menuItem,
    disabled,
    editOffer,
    disableOffer,
  }: Props) => {
    const dispatch = useDispatch()
    const { t, i18n } = useTranslation()
    const language = (i18n.resolvedLanguage || 'en') as SupportedLanguages

    const settings = useSelector(selectAppSettings)
    const currency = useSelector(selectCurrencyCode) || ''
    const selectedBranchId = useSelector(selectSelectedBranchId);
    const { isInReview, isVisible, name, _id, avatar, price } = menuItem

    const onToggleEditEnhanced = () => {
      if (_id && disableOffer) {
        disableOffer(_id)
      }
    }

    const [hideMenuItem] = useHideMenuItemMutation()
    const handleHide = useCallback(() => {
      hideMenuItem({ item_id: menuItem._id, storeId: selectedBranchId })
        .unwrap()
        .catch((error: IError) =>
          dispatch(
            addSystemNotification({
              message: error.message,
              intent: 'error'
            })
          )
        )
    }, [hideMenuItem, menuItem._id,selectedBranchId, dispatch])

    const [showMenuItem] = useShowMenuItemMutation()
    const handleShow = useCallback(() => {
      showMenuItem({  storeId: selectedBranchId, item_id: menuItem._id })
        .unwrap()
        .catch((error: IError) =>
          dispatch(
            addSystemNotification({
              message: error.message,
              intent: 'error'
            })
          )
        )
    }, [menuItem._id, showMenuItem, selectedBranchId])

    const handleToggle = useCallback(() => {
      isVisible ? handleHide() : handleShow()
    }, [handleHide, handleShow, isVisible])
    return (
      <ContentBlock
        data-testid={`menu-item-${menuItem?._id}`}
        className={clsx(className, Styles.wrapper, {
          [Styles.isHidden]: !isVisible,
          [Styles.isInReview]: isInReview
        })}>
        <div className={Styles.content}>
          <Avatar
            image={avatar}
            squared
            size="medium"
            string={name[language]}
          />
          <Text
            className={Styles.name}
            asChild
            variant="nextSubtitle"
            weight="extraBold">
            <p>
              {name[language]}
              {isInReview && <Alert />}
            </p>
          </Text>
          <Text
            className={Styles.price}
            asChild
            variant="nextSubtitle"
            weight="regular">
            <p>
              {formatPrice(price || 0, currency, {
                signDisplay: 'never'
              })}
            </p>
          </Text>
        </div>
        <div className={Styles.buttons}>
          {settings.confirmMenuHideAction && isVisible ? (
            <HideMenuModal onConfirm={handleToggle} name={name[language]}>
              <OptionButton
                disabled={disabled}
                icon={isVisible ? <EyeOff /> : <EyeOpen />}
                label={isVisible ? t('menu.item.hide') : t('menu.item.show')}
              />
            </HideMenuModal>
          ) : (
            <OptionButton
              disabled={disabled}
              onClick={handleToggle}
              icon={isVisible ? <EyeOff /> : <EyeOpen />}
              label={isVisible ? t('menu.item.hide') : t('menu.item.show')}
            />
          )}

          <OptionButton
            disabled={disabled}
            onClick={onToggleEditEnhanced}
            icon={<Edit />}
            label={t('menu.item.edit')}
          />
        </div>
      </ContentBlock>
    )
  }
)

MenuItem.displayName = 'MenuItem'
export default MenuItem
